import React, { useState, useEffect, useContext, useRef , useCallback} from 'react';
import {
  Building, Mail, Phone, Euro, MapPin, Plus, Search, Edit, Save, X,
  Users, Package, Eye, Trash2, AlertCircle, Check, Calendar,
  TrendingUp, FileText, Building2, UserPlus, Settings, ArrowUpDown,
  Filter, ChevronDown, ChevronUp, ChevronLeft, ChevronRight, Loader, ImageIcon,
  Barcode, Download, Upload, FileSpreadsheet
} from 'lucide-react';
import { AuthContext } from '../App';
import { useTheme } from '../contexts/ThemeContext';

const API_URL = process.env.REACT_APP_API_URL;

// ─── Modal: carga masiva de códigos de barras por proveedor (Excel) ──────────
const BarcodesBulkModal = ({ open, provider, theme, onClose }) => {
  const [downloading, setDownloading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);
  const fileInputRef = useRef(null);

  const token = localStorage.getItem('token');

  if (!open || !provider) return null;

  const handleDescargar = async () => {
    setDownloading(true);
    setError('');
    try {
      const r = await fetch(`${API_URL}/api/barcodes/excel/proveedor/${provider.id}`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (!r.ok) {
        const d = await r.json().catch(() => ({}));
        throw new Error(d.message || 'Error al descargar plantilla');
      }
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `barcodes_${(provider.nombre || 'proveedor').replace(/[^a-z0-9]/gi, '_')}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      setError(e.message);
    } finally {
      setDownloading(false);
    }
  };

  const handleSubir = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    setError('');
    setResult(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await fetch(`${API_URL}/api/barcodes/excel`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: fd,
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.message || 'Error al subir Excel');
      setResult(d);
    } catch (err) {
      setError(err.message);
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b" style={{ backgroundColor: theme.primaryLight }}>
          <div className="flex items-center gap-3">
            <Barcode size={22} style={{ color: theme.primary }} />
            <div>
              <h2 className="text-lg font-bold text-gray-800">Códigos de barras — carga masiva</h2>
              <p className="text-xs text-gray-600">{provider.nombre}</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-white rounded-lg"><X size={20} className="text-gray-500" /></button>
        </div>

        {error && (
          <div className="mx-6 mt-4 p-3 rounded-lg bg-red-50 border border-red-200 text-sm text-red-700 flex items-start gap-2">
            <AlertCircle size={16} className="mt-0.5" /><span>{error}</span>
          </div>
        )}

        <div className="flex-1 overflow-y-auto p-6 space-y-5">
          <ol className="list-decimal list-inside text-sm text-gray-700 space-y-2 bg-gray-50 border border-gray-200 rounded-lg p-4">
            <li>Descarga la plantilla — incluye todos los productos de este proveedor con sus códigos actuales.</li>
            <li>Edita las columnas <strong>codigo_unidad</strong>, <strong>codigo_caja</strong> y <strong>factor_caja</strong>. <span className="text-gray-500">No modifiques producto_id, nombre ni ref_proveedor.</span></li>
            <li>Sube el archivo. Si dejas una celda de código vacía, ese código se eliminará.</li>
            <li><strong>factor_caja</strong>: cuántas unidades contiene una caja. Por ejemplo, si la unidad base es "1 caja de 100", factor_caja = 100.</li>
          </ol>

          {/* Acción: descargar */}
          <div className="border-2 border-dashed border-gray-200 rounded-lg p-4">
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-3">
                <FileSpreadsheet size={24} className="text-emerald-600" />
                <div>
                  <p className="font-semibold text-gray-800 text-sm">Plantilla Excel</p>
                  <p className="text-xs text-gray-500">Productos del proveedor + barcodes actuales</p>
                </div>
              </div>
              <button
                onClick={handleDescargar}
                disabled={downloading}
                className="px-4 py-2 text-sm font-semibold text-white rounded shadow flex items-center gap-2 disabled:opacity-50"
                style={{ backgroundColor: theme.primary }}
              >
                {downloading ? <Loader size={14} className="animate-spin" /> : <Download size={14} />}
                Descargar
              </button>
            </div>
          </div>

          {/* Acción: subir */}
          <div className="border-2 border-dashed border-gray-200 rounded-lg p-4">
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-3">
                <Upload size={24} className="text-blue-600" />
                <div>
                  <p className="font-semibold text-gray-800 text-sm">Subir Excel editado</p>
                  <p className="text-xs text-gray-500">Crea o actualiza los códigos por producto. Si un código está repetido en otro producto, te avisamos.</p>
                </div>
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept=".xlsx,.xls"
                onChange={handleSubir}
                disabled={uploading}
                className="hidden"
                id="bulk-barcodes-file"
              />
              <label
                htmlFor="bulk-barcodes-file"
                className={`px-4 py-2 text-sm font-semibold rounded shadow flex items-center gap-2 cursor-pointer ${
                  uploading ? 'bg-gray-300 text-gray-500 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-700 text-white'
                }`}
              >
                {uploading ? <Loader size={14} className="animate-spin" /> : <Upload size={14} />}
                Seleccionar archivo
              </label>
            </div>
          </div>

          {result && (() => {
            const creados      = result.creados ?? 0;
            const actualizados = result.actualizados ?? 0;
            const eliminados   = result.eliminados ?? 0;
            const sinCambios   = result.sin_cambios ?? 0;
            const huboCambios  = creados + actualizados + eliminados > 0;
            return (
            <div className={`border rounded-lg p-4 text-sm ${huboCambios ? 'bg-emerald-50 border-emerald-200' : 'bg-blue-50 border-blue-200'}`}>
              <div className={`flex items-center gap-2 font-semibold mb-2 ${huboCambios ? 'text-emerald-800' : 'text-blue-800'}`}>
                <Check size={16} />
                {huboCambios
                  ? 'Procesado correctamente'
                  : 'Todo al día — no había cambios que aplicar'}
              </div>
              {huboCambios ? (
                <div className="grid grid-cols-3 gap-2 text-xs">
                  <div className="bg-white rounded p-2 text-center">
                    <div className="font-bold text-emerald-700 text-lg">{creados}</div>
                    <div className="text-gray-500">creados</div>
                  </div>
                  <div className="bg-white rounded p-2 text-center">
                    <div className="font-bold text-blue-700 text-lg">{actualizados}</div>
                    <div className="text-gray-500">actualizados</div>
                  </div>
                  <div className="bg-white rounded p-2 text-center">
                    <div className="font-bold text-red-700 text-lg">{eliminados}</div>
                    <div className="text-gray-500">eliminados</div>
                  </div>
                </div>
              ) : (
                <p className="text-xs text-blue-700">
                  {sinCambios > 0
                    ? `${sinCambios} código(s) ya estaban registrados con los mismos valores. No hace falta hacer nada.`
                    : 'No se encontraron códigos que aplicar en el archivo.'}
                </p>
              )}
              {result.conflictos && result.conflictos.length > 0 && (
                <div className="mt-3 bg-amber-50 border border-amber-200 rounded p-3">
                  <p className="text-amber-800 font-semibold text-xs mb-1">
                    {result.conflictos.length} conflicto(s) — código ya usado en otro producto:
                  </p>
                  <ul className="text-xs text-amber-700 list-disc list-inside max-h-32 overflow-y-auto">
                    {result.conflictos.map((c, i) => (
                      <li key={i}>
                        Producto <strong>{c.producto_id}</strong> · código <code className="bg-white px-1 rounded">{c.codigo}</code> ya está en producto <strong>{c.ya_asignado_a_producto_id}</strong>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {result.productos_no_encontrados && result.productos_no_encontrados.length > 0 && (
                <div className="mt-3 bg-red-50 border border-red-200 rounded p-3">
                  <p className="text-red-800 font-semibold text-xs mb-1">
                    {result.productos_no_encontrados.length} producto(s) no existen en el catálogo:
                  </p>
                  <ul className="text-xs text-red-700 list-disc list-inside max-h-32 overflow-y-auto">
                    {result.productos_no_encontrados.map((p, i) => (
                      <li key={i}>
                        Fila {p.fila}: producto <strong>{p.producto_id}</strong> no existe. Comprueba el ID o usa la plantilla descargada.
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {(result.filas_procesadas != null) && (
                <p className="text-xs text-gray-500 mt-3">
                  Se procesaron {result.filas_procesadas} fila(s) válida(s)
                  {result.filas_saltadas ? `, ${result.filas_saltadas} sin producto_id` : ''}
                  {sinCambios > 0 && huboCambios ? `, ${sinCambios} ya estaban al día` : ''}.
                </p>
              )}
            </div>
            );
          })()}
        </div>

        <div className="px-6 py-3 border-t bg-gray-50 flex justify-end">
          <button onClick={onClose} className="px-4 py-2 bg-white border border-gray-300 rounded text-sm font-medium text-gray-700 hover:bg-gray-100">
            Cerrar
          </button>
        </div>
      </div>
    </div>
  );
};

const ProductImage = React.memo(({ imagen, nombre, onImageClick }) => {
  const [imageError, setImageError] = useState(false)
  const [isLoading, setIsLoading] = useState(true)

  console.log("[v0] ProductImage - nombre:", nombre, "imagen:", imagen, "tipo:", typeof imagen)

  if (!imagen || imageError) {
    console.log("[v0] ProductImage - NO SE MUESTRA porque imagen es:", imagen, "o imageError es:", imageError)
    return (
      <div className="w-12 h-12 bg-gray-100 rounded flex items-center justify-center">
        <ImageIcon size={16} className="text-gray-400" />
      </div>
    )
  }

  return (
    <div className="w-12 h-12 relative">
      {isLoading && (
        <div className="absolute inset-0 bg-gray-200 animate-pulse rounded flex items-center justify-center">
          <ImageIcon size={16} className="text-gray-400" />
        </div>
      )}
      <img
        src={imagen || "/placeholder.svg"}
        alt={nombre}
        className={`w-full h-full object-cover rounded cursor-pointer hover:opacity-80 transition-opacity ${
          isLoading ? 'opacity-0' : 'opacity-100'
        }`}
        onLoad={() => {
          console.log("[v0] Imagen cargada correctamente:", nombre)
          setIsLoading(false)
        }}
        onError={() => {
          console.log("[v0] Error al cargar imagen:", nombre, imagen)
          setImageError(true)
          setIsLoading(false)
        }}
        onClick={() => onImageClick(imagen, nombre)}
        title="Click para ver en grande"
      />
    </div>
  )
})

const ImageModal = React.memo(({ isOpen, imageSrc, imageAlt, onClose }) => {
  const modalRef = useRef(null)

  console.log("[v0] ImageModal renderizado - isOpen:", isOpen, "imageSrc:", imageSrc)

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') onClose()
    }
    const handleClickOutside = (e) => {
      if (modalRef.current && !modalRef.current.contains(e.target)) onClose()
    }
    if (isOpen) {
      document.addEventListener('keydown', handleEscape)
      document.addEventListener('mousedown', handleClickOutside)
      document.body.style.overflow = 'hidden'
    }
    return () => {
      document.removeEventListener('keydown', handleEscape)
      document.removeEventListener('mousedown', handleClickOutside)
      document.body.style.overflow = 'auto'
    }
  }, [isOpen, onClose])

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
      <div
        ref={modalRef}
        className="relative max-w-4xl max-h-full bg-white rounded-lg shadow-xl overflow-hidden"
      >
        <div className="absolute top-4 right-4 z-10">
          <button
            onClick={onClose}
            className="bg-black bg-opacity-50 text-white p-2 rounded-full hover:bg-opacity-70 transition-all"
          >
            <X size={20} />
          </button>
        </div>
        <div className="p-4">
          <img
            src={imageSrc || "/placeholder.svg"}
            alt={imageAlt}
            className="max-w-full max-h-[80vh] object-contain mx-auto rounded"
            onError={(e) => { e.target.style.display = 'none' }}
          />
          <div className="mt-4 text-center">
            <h3 className="text-lg font-medium text-gray-800">{imageAlt}</h3>
          </div>
        </div>
      </div>
    </div>
  )
})

export default function ProviderManagement() {
  const { user } = useContext(AuthContext);

  // ─── TEMA DINÁMICO ───────────────────────────────────────────────────
  const { theme } = useTheme();
  const bgPrimary = { backgroundColor: theme.primary };
  const bgPrimaryLight = { backgroundColor: theme.primaryLight };
  const bgPrimaryDark = { backgroundColor: theme.primaryDark };
  const textPrimary = { color: theme.primary };
  const textPrimaryDark = { color: theme.primaryDark };
  const borderPrimary = { borderColor: theme.primary };
  // ─────────────────────────────────────────────────────────────────────

  const isAdmin = user?.permisos === 1;
  const isClient = user?.permisos === 5;
  const isProveedor = user?.permisos === 4;

  const [providers, setProviders] = useState([]);
  const [activeProvider, setActiveProvider] = useState(null);
  const [showProvidersList, setShowProvidersList] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [message, setMessage] = useState({ text: '', type: '' });

  const [searchTerm, setSearchTerm] = useState('');
  const [filteredProviders, setFilteredProviders] = useState([]);

  // Modal de bulk Excel de códigos de barras
  const [barcodesBulkModal, setBarcodesBulkModal] = useState({ open: false, provider: null });

  const [viewMode, setViewMode] = useState('cards');
  const [sortField, setSortField] = useState('fecha_registro');
  const [sortDirection, setSortDirection] = useState('desc');
  const [currentPage, setCurrentPage] = useState(1);
  const [providersPerPage] = useState(20);

  const [modalImagenVisible, setModalImagenVisible] = useState(false)
  const [imagenSeleccionada, setImagenSeleccionada] = useState({ src: "", alt: "" })

  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingProvider, setEditingProvider] = useState(null);
  const [formData, setFormData] = useState({
    nombre: '',
    direccion: '',
    telefono: '',
    email: '',
    precio_min: '',
    requiere_aprobacion_matriz: false
  });
  const [formErrors, setFormErrors] = useState({});

  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [productSearchTerm, setProductSearchTerm] = useState('');
  const [productSortField, setProductSortField] = useState('nombre');
  const [productSortDirection, setProductSortDirection] = useState('asc');
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [productFilters, setProductFilters] = useState({
    precio_min: '',
    precio_max: '',
    marca: '',
    familia: ''
  });

  const [currentProductPage, setCurrentProductPage] = useState(1);
  const [productsPerPage] = useState(50);
  const [totalProducts, setTotalProducts] = useState(0);
  const [totalProductPages, setTotalProductPages] = useState(0);

  useEffect(() => {
    loadProviders();
    loadAllProducts();
  }, []);

  useEffect(() => {
    filterAndSortProviders();
  }, [providers, searchTerm, sortField, sortDirection]);

  useEffect(() => {
    filterAndSortProducts();
  }, [products, productSearchTerm, productSortField, productSortDirection, productFilters]);

  useEffect(() => {
    if (isAdmin && !activeProvider) {
      loadAllProducts();
    }
  }, [currentProductPage]);

  const filterAndSortProviders = () => {
    let filtered = [...providers];
    if (searchTerm.trim()) {
      filtered = filtered.filter(provider =>
        provider.nombre?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        provider.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        provider.telefono?.includes(searchTerm)
      );
    }
    filtered.sort((a, b) => {
      let aValue = a[sortField];
      let bValue = b[sortField];
      if (aValue === null || aValue === undefined) aValue = '';
      if (bValue === null || bValue === undefined) bValue = '';
      aValue = aValue.toString().toLowerCase();
      bValue = bValue.toString().toLowerCase();
      if (sortDirection === 'asc') return aValue.localeCompare(bValue);
      else return bValue.localeCompare(aValue);
    });
    setFilteredProviders(filtered);
    setCurrentPage(1);
  };

  const handleImageClick = useCallback((imageSrc, imageAlt) => {
    console.log("[v0] handleImageClick llamado con:", { imageSrc, imageAlt })
    setImagenSeleccionada({ src: imageSrc, alt: imageAlt })
    setModalImagenVisible(true)
  }, [])

  const handleCloseImageModal = useCallback(() => {
    setModalImagenVisible(false)
    setImagenSeleccionada({ src: "", alt: "" })
  }, [])

  const filterAndSortProducts = () => {
    let filtered = [...products];
    if (productSearchTerm.trim()) {
      if (isClient) {
        const searchWords = productSearchTerm.toLowerCase().trim().split(/\s+/);
        filtered = filtered.filter(product => {
          const productName = product.nombre?.toLowerCase() || '';
          return searchWords.every(word => productName.includes(word));
        });
      } else {
        const searchTerms = productSearchTerm.toLowerCase().split(' ');
        filtered = filtered.filter(product =>
          searchTerms.every(term =>
            product.nombre?.toLowerCase().includes(term) ||
            product.ref_proveedor?.toLowerCase().includes(term) ||
            product.marca?.toLowerCase().includes(term) ||
            product.familia?.toLowerCase().includes(term)
          )
        );
      }
    }
    if (productFilters.precio_min) {
      filtered = filtered.filter(product =>
        parseFloat(product.precio_coste || 0) >= parseFloat(productFilters.precio_min)
      );
    }
    if (productFilters.precio_max) {
      filtered = filtered.filter(product =>
        parseFloat(product.precio_coste || 0) <= parseFloat(productFilters.precio_max)
      );
    }
    if (productFilters.marca) {
      filtered = filtered.filter(product =>
        product.marca?.toLowerCase().includes(productFilters.marca.toLowerCase())
      );
    }
    if (productFilters.familia) {
      filtered = filtered.filter(product =>
        product.familia?.toLowerCase().includes(productFilters.familia.toLowerCase())
      );
    }
    filtered.sort((a, b) => {
      let aValue = a[productSortField];
      let bValue = b[productSortField];
      if (aValue === null || aValue === undefined) aValue = '';
      if (bValue === null || bValue === undefined) bValue = '';
      if (productSortField === 'precio_coste') {
        aValue = parseFloat(aValue) || 0;
        bValue = parseFloat(bValue) || 0;
        return productSortDirection === 'asc' ? aValue - bValue : bValue - aValue;
      }
      aValue = aValue.toString().toLowerCase();
      bValue = bValue.toString().toLowerCase();
      if (productSortDirection === 'asc') return aValue.localeCompare(bValue);
      else return bValue.localeCompare(aValue);
    });
    setFilteredProducts(filtered);
    setTotalProducts(filtered.length);
    setTotalProductPages(Math.ceil(filtered.length / productsPerPage));
    if (currentProductPage > Math.ceil(filtered.length / productsPerPage)) {
      setCurrentProductPage(1);
    }
  };

  const handleProductSort = (field) => {
    if (productSortField === field) {
      setProductSortDirection(productSortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setProductSortField(field);
      setProductSortDirection('asc');
    }
  };

  const getCurrentPageProducts = () => {
    const startIndex = (currentProductPage - 1) * productsPerPage;
    const endIndex = startIndex + productsPerPage;
    return filteredProducts.slice(startIndex, endIndex);
  };

  const handleProductPageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalProductPages) {
      setCurrentProductPage(newPage);
    }
  };

  const getPageNumbers = () => {
    const pages = [];
    const maxVisiblePages = 5;
    let startPage = Math.max(1, currentProductPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalProductPages, startPage + maxVisiblePages - 1);
    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }
    for (let i = startPage; i <= endPage; i++) pages.push(i);
    return pages;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('es-ES');
  };

  const formatPrice = (price) => {
    if (price === null || price === undefined) return 'N/A';
    return `€${parseFloat(price).toFixed(2)}`;
  };

  const highlightSearchTerm = (text, searchTerm) => {
    if (!searchTerm.trim() || !text) return text;
    const searchTerms = searchTerm.toLowerCase().split(' ');
    let highlightedText = text;
    searchTerms.forEach(term => {
      if (term) {
        const regex = new RegExp(`(${term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
        highlightedText = highlightedText.replace(regex, '<mark>$1</mark>');
      }
    });
    return <span dangerouslySetInnerHTML={{ __html: highlightedText }} />;
  };

  const indexOfLastProvider = currentPage * providersPerPage;
  const indexOfFirstProvider = indexOfLastProvider - providersPerPage;
  const currentProviders = filteredProviders.slice(indexOfFirstProvider, indexOfLastProvider);
  const totalPages = Math.ceil(filteredProviders.length / providersPerPage);

  const loadProviders = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      let endpoint = `${API_URL}/api/proveedores`;
      if (isProveedor) endpoint = `${API_URL}/api/proveedores/mi-info`;
      const response = await fetch(endpoint, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) throw new Error('Error al cargar proveedores');
      const data = await response.json();
      if (isProveedor) {
        setProviders([data.proveedor]);
        setFilteredProviders([data.proveedor]);
      } else {
        setProviders(data.proveedores);
        setFilteredProviders(data.proveedores);
      }
    } catch (error) {
      console.error('Error al cargar proveedores:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const loadAllProducts = async () => {
    try {
      setLoadingProducts(true);
      const token = localStorage.getItem('token');
      let endpoint;
      let params = new URLSearchParams();
      if (isAdmin) {
        endpoint = `${API_URL}/api/productos/all-unique`;
        params.append('page', currentProductPage);
        params.append('limit', productsPerPage);
      } else if (isClient) {
        endpoint = `${API_URL}/api/productos/cliente-proveedores`;
        params.append('page', 1);
        params.append('limit', 1000);
      } else if (isProveedor) {
        endpoint = `${API_URL}/api/productos/mis-productos`;
        params.append('page', 1);
        params.append('limit', 1000);
      }
      const response = await fetch(`${endpoint}?${params}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) throw new Error('Error al cargar productos');
      const data = await response.json();
      let productos = data.productos || [];
      if (isAdmin) {
        productos = productos.filter(product =>
          product.nombre && product.nombre.trim() !== '' &&
          product.proveedor_nombre && product.proveedor_nombre.trim() !== ''
        );
      }
      setProducts(productos);
      console.log("[v0] Productos cargados:", productos.length);
      console.log("[v0] Primer producto:", productos[0]);
      console.log("[v0] Campos del primer producto:", productos[0] ? Object.keys(productos[0]) : 'No hay productos');
      if (isAdmin) {
        setTotalProducts(data.total || 0);
        setTotalProductPages(data.totalPages || 0);
      }
    } catch (error) {
      console.error('Error al cargar productos:', error);
      setError(error.message);
    } finally {
      setLoadingProducts(false);
    }
  };

  const loadProviderProducts = async (providerId) => {
    try {
      setLoadingProducts(true);
      const token = localStorage.getItem('token');
      let endpoint;
      if (isAdmin) endpoint = `${API_URL}/api/productos/proveedor/${providerId}/unique?page=1&limit=100000`;
      else if (isClient) endpoint = `${API_URL}/api/productos/proveedor/${providerId}/cliente`;
      else if (isProveedor) endpoint = `${API_URL}/api/productos/mis-productos`;
      const response = await fetch(endpoint, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) throw new Error('Error al cargar productos del proveedor');
      const data = await response.json();
      let productos = data.productos || [];
      productos = productos.filter(product => product.nombre && product.nombre.trim() !== '');
      setProducts(productos);
      setCurrentProductPage(1);
    } catch (error) {
      console.error('Error al cargar productos del proveedor:', error);
      setError(error.message);
    } finally {
      setLoadingProducts(false);
    }
  };

  const handleProviderSelected = (provider) => {
    setActiveProvider(provider);
    setShowProvidersList(false);
    loadProviderProducts(provider.id);
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (formErrors[field]) setFormErrors(prev => ({ ...prev, [field]: '' }));
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.nombre.trim()) newErrors.nombre = 'El nombre es requerido';
    if (formData.email && !/\S+@\S+\.\S+/.test(formData.email)) newErrors.email = 'El email no es válido';
    if (formData.telefono && !/^\+?[\d\s\-()]+$/.test(formData.telefono)) newErrors.telefono = 'El teléfono no tiene un formato válido';
    if (formData.precio_min && (isNaN(formData.precio_min) || parseFloat(formData.precio_min) < 0)) newErrors.precio_min = 'El precio mínimo debe ser un número válido mayor o igual a 0';
    setFormErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleCreateProvider = async () => {
    if (!validateForm()) { showMessage('Por favor corrige los errores en el formulario', 'danger'); return; }
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const providerData = {
        nombre: formData.nombre.trim(),
        direccion: formData.direccion.trim(),
        telefono: formData.telefono.trim(),
        email: formData.email.trim(),
        precio_min: formData.precio_min ? parseFloat(formData.precio_min) : null,
        requiere_aprobacion_matriz: formData.requiere_aprobacion_matriz ? 1 : 0
      };
      const response = await fetch(`${API_URL}/api/proveedores`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(providerData)
      });
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Error al crear proveedor');
      }
      showMessage('Proveedor creado correctamente', 'success');
      setFormData({ nombre: '', direccion: '', telefono: '', email: '', precio_min: '', requiere_aprobacion_matriz: false });
      setShowCreateForm(false);
      loadProviders();
    } catch (error) {
      console.error('Error al crear proveedor:', error);
      showMessage(error.message || 'Error al crear el proveedor', 'danger');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateProvider = async () => {
    if (!validateForm()) { showMessage('Por favor corrige los errores en el formulario', 'danger'); return; }
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const providerData = {
        nombre: formData.nombre.trim(),
        direccion: formData.direccion.trim(),
        telefono: formData.telefono.trim(),
        email: formData.email.trim(),
        precio_min: formData.precio_min ? parseFloat(formData.precio_min) : null,
        requiere_aprobacion_matriz: formData.requiere_aprobacion_matriz ? 1 : 0
      };
      const response = await fetch(`${API_URL}/api/proveedores/${editingProvider.id}`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(providerData)
      });
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Error al actualizar proveedor');
      }
      showMessage('Proveedor actualizado correctamente', 'success');
      if (activeProvider && activeProvider.id === editingProvider.id) {
        setActiveProvider({...activeProvider, ...providerData});
      }
      setEditingProvider(null);
      setFormData({ nombre: '', direccion: '', telefono: '', email: '', precio_min: '', requiere_aprobacion_matriz: false });
      loadProviders();
    } catch (error) {
      console.error('Error al actualizar proveedor:', error);
      showMessage(error.message || 'Error al actualizar el proveedor', 'danger');
    } finally {
      setLoading(false);
    }
  };

  const startEditProvider = (provider) => {
    setEditingProvider(provider);
    setFormData({
      nombre: provider.nombre || '',
      direccion: provider.direccion || '',
      telefono: provider.telefono || '',
      email: provider.email || '',
      precio_min: provider.precio_min || '',
      // Llega del LISTADO de proveedores (GET /api/proveedores), no del detalle.
      requiere_aprobacion_matriz: !!Number(provider.requiere_aprobacion_matriz)
    });
  };

  const cancelEdit = () => {
    setEditingProvider(null);
    setShowCreateForm(false);
    setFormData({ nombre: '', direccion: '', telefono: '', email: '', precio_min: '', requiere_aprobacion_matriz: false });
    setFormErrors({});
  };

  const showMessage = (text, type) => {
    setMessage({ text, type });
    setTimeout(() => setMessage({ text: '', type: '' }), 5000);
  };

  const handleBackToList = () => {
    setShowProvidersList(true);
    setActiveProvider(null);
    setCurrentProductPage(1);
    loadAllProducts();
    cancelEdit();
  };

  // ─── Componente de paginación reutilizable ────────────────────────────
  const PaginationControls = () => (
    <div className="mt-6 flex items-center justify-between border-t border-gray-200 bg-white px-4 py-3 sm:px-6">
      <div className="flex flex-1 justify-between sm:hidden">
        <button
          onClick={() => handleProductPageChange(currentProductPage - 1)}
          disabled={currentProductPage === 1}
          className="relative inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Anterior
        </button>
        <button
          onClick={() => handleProductPageChange(currentProductPage + 1)}
          disabled={currentProductPage === totalProductPages}
          className="relative ml-3 inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Siguiente
        </button>
      </div>
      <div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
        <p className="text-sm text-gray-700">
          Mostrando{' '}
          <span className="font-medium">{((currentProductPage - 1) * productsPerPage) + 1}</span>{' '}
          a{' '}
          <span className="font-medium">{Math.min(currentProductPage * productsPerPage, totalProducts)}</span>{' '}
          de{' '}
          <span className="font-medium">{totalProducts}</span> productos
        </p>
        <nav className="isolate inline-flex -space-x-px rounded-md shadow-sm">
          <button
            onClick={() => handleProductPageChange(currentProductPage - 1)}
            disabled={currentProductPage === 1}
            className="relative inline-flex items-center rounded-l-md px-2 py-2 text-gray-400 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <ChevronLeft className="h-5 w-5" />
          </button>
          {getPageNumbers().map((pageNum) => (
            <button
              key={pageNum}
              onClick={() => handleProductPageChange(pageNum)}
              className={`relative inline-flex items-center px-4 py-2 text-sm font-semibold ring-1 ring-inset ring-gray-300 ${
                pageNum === currentProductPage
                  ? 'z-10 text-white'
                  : 'text-gray-900 hover:bg-gray-50'
              }`}
              style={pageNum === currentProductPage ? bgPrimary : {}}
            >
              {pageNum}
            </button>
          ))}
          <button
            onClick={() => handleProductPageChange(currentProductPage + 1)}
            disabled={currentProductPage === totalProductPages}
            className="relative inline-flex items-center rounded-r-md px-2 py-2 text-gray-400 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <ChevronRight className="h-5 w-5" />
          </button>
        </nav>
      </div>
    </div>
  );

  // ─── Loading screen ───────────────────────────────────────────────────
  if (loading && providers.length === 0) {
    return (
      <div className="fixed inset-0 top-16 bg-gradient-to-br from-gray-50 to-gray-100 flex justify-center items-center z-50">
        <div className="text-center">
          <div className="relative mb-8">
            <div
              className="w-20 h-20 border-4 rounded-full animate-spin mx-auto"
              style={{ borderColor: `${theme.primary}40`, borderTopColor: theme.primary }}
            ></div>
            <Building
              className="w-8 h-8 absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2"
              style={textPrimary}
            />
          </div>
          <h3 className="text-xl font-bold mb-2" style={textPrimary}>Cargando proveedores</h3>
          <p className="text-gray-600">Preparando sistema de gestión...</p>
        </div>
      </div>
    );
  }

  // ─────────────────────────────────────────────────────────────────────
  // VISTA PARA CLIENTES
  // ─────────────────────────────────────────────────────────────────────
  if (isClient) {
    return (
      <div className="fixed inset-0 top-16 bg-gradient-to-br from-gray-50 to-gray-100 overflow-hidden">
        <div className="h-full flex flex-col">

          {/* Header */}
          <div className="flex-shrink-0 bg-white shadow-sm border-b border-gray-200 p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <div className="p-2 rounded-lg mr-3" style={bgPrimaryLight}>
                  <Package size={20} style={textPrimary} />
                </div>
                <div>
                  <h1 className="text-lg font-bold text-gray-800">Catálogo de Productos</h1>
                  <p className="text-sm text-gray-600">Productos disponibles de sus proveedores asociados</p>
                </div>
              </div>
              <div className="flex items-center space-x-2">
                <div className="text-right">
                  <div className="text-sm font-medium text-gray-900">
                    {filteredProducts.length} productos encontrados
                  </div>
                  <div className="text-xs text-gray-500">
                    Total disponible: {totalProducts} productos
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Mensaje de estado */}
          {message.text && (
            <div className="px-4 pt-4">
              <div className={`p-3 rounded-lg flex items-center shadow-lg border text-sm ${
                message.type === 'success' ? 'border-emerald-200' : 'border-red-200'
              }`}
              style={{
                background: message.type === 'success' ? 'linear-gradient(135deg, #ecfdf5, #d1fae5)' : 'linear-gradient(135deg, #fef2f2, #fecaca)',
                color: message.type === 'success' ? '#065f46' : '#991b1b'
              }}>
                {message.type === 'success' ? (
                  <Check className="w-4 h-4 mr-2 flex-shrink-0" />
                ) : (
                  <AlertCircle className="w-4 h-4 mr-2 flex-shrink-0" />
                )}
                <span className="font-medium flex-1">{message.text}</span>
                <button onClick={() => setMessage({ text: '', type: '' })} className="ml-auto p-1 hover:bg-white hover:bg-opacity-30 rounded">
                  <X className="w-3 h-3" />
                </button>
              </div>
            </div>
          )}

          {/* Contenido Principal */}
          <div className="flex-1 overflow-auto">
            <div className="p-6">

              {/* Filtros */}
              <div className="bg-white rounded-xl shadow-lg border border-gray-100 mb-6">
                <div className="p-6">
                  <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                    <Filter size={20} className="mr-2" style={textPrimary} />
                    Filtros de búsqueda
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-2">Buscar producto</label>
                      <input
                        type="text"
                        value={productSearchTerm}
                        onChange={(e) => setProductSearchTerm(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2"
                        placeholder="Ej: GASA 45..."
                      />
                      <p className="text-xs text-gray-500 mt-1">Búsqueda estricta por nombre de producto</p>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-2">Precio mínimo (€)</label>
                      <input
                        type="number"
                        value={productFilters.precio_min}
                        onChange={(e) => setProductFilters(prev => ({...prev, precio_min: e.target.value}))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2"
                        placeholder="0.00" step="0.01" min="0"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-2">Precio máximo (€)</label>
                      <input
                        type="number"
                        value={productFilters.precio_max}
                        onChange={(e) => setProductFilters(prev => ({...prev, precio_max: e.target.value}))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2"
                        placeholder="999.99" step="0.01" min="0"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-2">Marca</label>
                      <input
                        type="text"
                        value={productFilters.marca}
                        onChange={(e) => setProductFilters(prev => ({...prev, marca: e.target.value}))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2"
                        placeholder="Filtrar por marca..."
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-2">Familia</label>
                      <input
                        type="text"
                        value={productFilters.familia}
                        onChange={(e) => setProductFilters(prev => ({...prev, familia: e.target.value}))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2"
                        placeholder="Filtrar por familia..."
                      />
                    </div>
                    {(productSearchTerm || productFilters.precio_min || productFilters.precio_max || productFilters.marca || productFilters.familia) && (
                      <div className="md:col-span-5">
                        <button
                          onClick={() => {
                            setProductSearchTerm('');
                            setProductFilters({ precio_min: '', precio_max: '', marca: '', familia: '' });
                          }}
                          className="text-gray-600 hover:text-gray-800 text-sm flex items-center"
                        >
                          <X size={16} className="mr-1" />
                          Limpiar filtros
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Tabla de productos */}
              <div className="bg-white rounded-xl shadow-lg border border-gray-100">
                <div className="p-6">
                  <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                    <Package size={20} className="mr-2" style={textPrimary} />
                    Lista de Productos
                    <span className="ml-2 text-xs px-2 py-1 rounded-full" style={{ backgroundColor: theme.primaryLight, color: theme.primaryDark }}>
                      {filteredProducts.length} productos
                    </span>
                  </h3>

                  {loadingProducts ? (
                    <div className="text-center py-12">
                      <div
                        className="w-12 h-12 border-4 border-t-transparent rounded-full animate-spin mx-auto mb-4"
                        style={{ borderColor: theme.primary, borderTopColor: 'transparent' }}
                      ></div>
                      <p className="text-gray-600 text-lg">Cargando productos...</p>
                    </div>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full table-auto divide-y divide-gray-200">
                        <thead>
                          <tr style={bgPrimary}>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">#</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Imagen</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100" onClick={() => handleProductSort('nombre')}>
                              <div className="flex items-center">
                                Producto <ArrowUpDown size={14} className="ml-1" />
                                {productSortField === 'nombre' && (productSortDirection === 'asc' ? <ChevronUp size={14} /> : <ChevronDown size={14} />)}
                              </div>
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100" onClick={() => handleProductSort('ref_proveedor')}>
                              <div className="flex items-center">Referencia <ArrowUpDown size={14} className="ml-1" /></div>
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100" onClick={() => handleProductSort('marca')}>
                              <div className="flex items-center">Marca <ArrowUpDown size={14} className="ml-1" /></div>
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100" onClick={() => handleProductSort('familia')}>
                              <div className="flex items-center">Familia <ArrowUpDown size={14} className="ml-1" /></div>
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Unidad</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Embalaje</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100" onClick={() => handleProductSort('precio_coste')}>
                              <div className="flex items-center">
                                Precio (€) <ArrowUpDown size={14} className="ml-1" />
                                {productSortField === 'precio_coste' && (productSortDirection === 'asc' ? <ChevronUp size={14} /> : <ChevronDown size={14} />)}
                              </div>
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Proveedor</th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {getCurrentPageProducts().map((product, index) => (
                            <tr key={product.id} className="hover:bg-gray-50 transition-colors duration-150">
                              <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                                {((currentProductPage - 1) * productsPerPage) + index + 1}
                              </td>
                              <td className="px-4 py-4 whitespace-nowrap">
                                {console.log("[v0] Producto completo:", product)}
                                {console.log("[v0] Campo imagen:", product.imagen)}
                                <ProductImage
                                  imagen={product.imagen}
                                  nombre={product.nombre}
                                  onImageClick={handleImageClick}
                                />
                              </td>
                              <td className="px-4 py-4 text-sm text-gray-900">
                                <strong>{highlightSearchTerm(product.nombre, productSearchTerm)}</strong>
                              </td>
                              <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-600">
                                {highlightSearchTerm(product.ref_proveedor || '-', productSearchTerm)}
                              </td>
                              <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-600">
                                {highlightSearchTerm(product.marca || '-', productSearchTerm)}
                              </td>
                              <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-600">
                                {highlightSearchTerm(product.familia || '-', productSearchTerm)}
                              </td>
                              <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-600">{product.unidadbasica || '-'}</td>
                              <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-600">{product.cantidad_embalaje || '-'}</td>
                              <td className="px-4 py-4 whitespace-nowrap text-sm font-bold" style={{ color: theme.primaryDark, backgroundColor: theme.primaryLight }}>
                                {formatPrice(product.precio_coste)}
                              </td>
                              <td className="px-4 py-4 whitespace-nowrap">
                                <span className="inline-block text-white text-xs px-2 py-1 rounded text-center" style={bgPrimary}>
                                  {product.proveedor_nombre || 'N/A'}
                                </span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>

                      {filteredProducts.length === 0 && !loadingProducts && (
                        <div className="text-center py-12 text-gray-500">
                          <Package size={48} className="mx-auto mb-4 text-gray-400" />
                          <p className="text-lg font-medium">No se encontraron productos</p>
                          {productSearchTerm && (
                            <p className="text-sm mt-2">No se encontraron productos que contengan: <strong>"{productSearchTerm}"</strong></p>
                          )}
                          <p className="text-sm mt-2">Intente modificar los filtros o contacte con su proveedor.</p>
                        </div>
                      )}
                    </div>
                  )}

                  {totalProductPages > 1 && <PaginationControls />}

                  {filteredProducts.length > 0 && (
                    <div className="mt-6 p-4 rounded-lg" style={bgPrimaryLight}>
                      <div className="flex justify-between items-center">
                        <div className="flex items-center text-sm">
                          <Check className="w-4 h-4 mr-2" style={textPrimary} />
                          <span className="font-medium" style={textPrimaryDark}>
                            Total de productos encontrados: {filteredProducts.length}
                          </span>
                        </div>
                        {productSearchTerm && (
                          <span className="text-sm" style={textPrimary}>Búsqueda: "{productSearchTerm}"</span>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ─────────────────────────────────────────────────────────────────────
  // VISTA PARA ADMIN Y PROVEEDOR
  // ─────────────────────────────────────────────────────────────────────
  return (
    <div className="fixed inset-x-0 top-16 bottom-0 bg-gray-50 flex overflow-hidden">

      {/* ── Sidebar (estilo Stock) ──────────────────────────────────────── */}
      {isAdmin && (
        <aside className="w-80 bg-white border-r border-gray-200 flex flex-col flex-shrink-0">
          {/* Header del panel */}
          <div className="p-4 border-b" style={bgPrimaryLight}>
            <h2 className="font-bold text-gray-800 flex items-center gap-2">
              <Building size={18} style={textPrimary} />
              Proveedores
            </h2>
            <p className="text-xs text-gray-600 mt-1">
              {filteredProviders.length} proveedores registrados
            </p>
          </div>

          {/* Búsqueda */}
          <div className="p-3 border-b">
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Search size={16} className="text-gray-400" />
              </div>
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Buscar proveedor..."
                className="w-full pr-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none transition-colors"
                style={{ paddingLeft: '40px' }}
                onFocus={(e) => e.target.style.borderColor = theme.primary}
                onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
              />
            </div>
          </div>

          {/* Nuevo proveedor */}
          <div className="p-3 border-b">
            <button
              onClick={() => setShowCreateForm(true)}
              className="w-full text-white py-2 px-4 rounded-lg transition-colors flex items-center justify-center text-sm font-medium"
              style={bgPrimary}
              onMouseEnter={e => e.currentTarget.style.backgroundColor = theme.primaryDark}
              onMouseLeave={e => e.currentTarget.style.backgroundColor = theme.primary}
            >
              <Plus size={16} className="mr-2" />
              Nuevo Proveedor
            </button>
          </div>

          {/* Lista de proveedores */}
          <div className="flex-1 overflow-y-auto">
            {filteredProviders.length === 0 ? (
              <div className="p-8 text-center text-gray-400 text-sm">
                {searchTerm ? 'Sin coincidencias' : 'Sin proveedores disponibles'}
              </div>
            ) : (
              filteredProviders.map(provider => {
                const selected = activeProvider && activeProvider.id === provider.id;
                return (
                  <div
                    key={provider.id}
                    className="group relative border-b transition-colors"
                    style={selected
                      ? { backgroundColor: theme.primaryLight, borderLeft: `4px solid ${theme.primary}` }
                      : { borderLeft: '4px solid transparent' }}
                    onMouseEnter={(e) => !selected && (e.currentTarget.style.backgroundColor = theme.primaryLight)}
                    onMouseLeave={(e) => !selected && (e.currentTarget.style.backgroundColor = '')}
                  >
                    <button
                      onClick={() => handleProviderSelected(provider)}
                      className="w-full text-left p-3 pr-16"
                    >
                      <div className="font-semibold text-gray-800 text-sm truncate">{provider.nombre}</div>
                      <div className="text-xs text-gray-500 mt-0.5 space-y-0.5">
                        {provider.email && (
                          <div className="flex items-center gap-1 truncate"><Mail size={10} className="flex-shrink-0" />{provider.email}</div>
                        )}
                        {provider.telefono && (
                          <div className="flex items-center gap-1"><Phone size={10} className="flex-shrink-0" />{provider.telefono}</div>
                        )}
                        {provider.precio_min && (
                          <div className="font-medium" style={textPrimary}>Mín: €{provider.precio_min}</div>
                        )}
                      </div>
                    </button>
                    <div className="absolute top-2 right-2 flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      <button
                        onClick={(e) => { e.stopPropagation(); setBarcodesBulkModal({ open: true, provider }); }}
                        className="p-1.5 rounded-md hover:bg-white"
                        title="Códigos de barras (carga masiva por Excel)"
                      >
                        <Barcode size={14} style={textPrimary} />
                      </button>
                      <button
                        onClick={(e) => { e.stopPropagation(); startEditProvider(provider); }}
                        className="p-1.5 rounded-md hover:bg-white"
                        title="Editar proveedor"
                      >
                        <Edit size={14} className="text-gray-500" />
                      </button>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </aside>
      )}

      {/* ── Main Content ─────────────────────────────────────────────────── */}
      <main className="flex-1 flex flex-col overflow-hidden">

        {/* Header (estilo Gestión de Pedidos) */}
        <div className="flex-shrink-0 bg-white shadow-sm border-b border-gray-200 p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center min-w-0">
              <div className="p-2 rounded-lg mr-3 flex-shrink-0" style={bgPrimaryLight}>
                <Building size={20} style={textPrimary} />
              </div>
              <div className="min-w-0">
                <h1 className="text-lg font-bold text-gray-800 truncate">
                  {activeProvider ? activeProvider.nombre : (isProveedor ? 'Mi Información' : 'Gestión de Proveedores')}
                </h1>
                <p className="text-sm text-gray-600 truncate">
                  {activeProvider
                    ? ([activeProvider.email, activeProvider.telefono, activeProvider.direccion, activeProvider.precio_min ? `Mín: €${activeProvider.precio_min}` : null].filter(Boolean).join('  ·  ') || 'Catálogo de productos')
                    : (isProveedor ? 'Tu información como proveedor' : 'Administra proveedores y sus catálogos')}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-4 flex-shrink-0">
              {activeProvider && (
                <div className="text-right">
                  <div className="text-sm font-medium text-gray-900">{filteredProducts.length} productos</div>
                  {productSearchTerm && <div className="text-xs text-gray-500 truncate max-w-[160px]">"{productSearchTerm}"</div>}
                </div>
              )}
              {activeProvider && isAdmin && (
                <button
                  onClick={handleBackToList}
                  className="inline-flex items-center px-3 py-2 text-sm font-medium text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                  title="Volver al listado"
                >
                  <X size={16} className="mr-1.5" /> Cerrar
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Contenido */}
        <div className="flex-1 overflow-auto p-6">

          {/* Mensaje de estado */}
          {message.text && (
            <div className="mb-4">
              <div className={`p-3 rounded-lg flex items-center shadow-lg border text-sm ${
                message.type === 'success' ? 'border-emerald-200' : 'border-red-200'
              }`}
              style={{
                background: message.type === 'success' ? 'linear-gradient(135deg, #ecfdf5, #d1fae5)' : 'linear-gradient(135deg, #fef2f2, #fecaca)',
                color: message.type === 'success' ? '#065f46' : '#991b1b'
              }}>
                {message.type === 'success' ? <Check className="w-4 h-4 mr-2 flex-shrink-0" /> : <AlertCircle className="w-4 h-4 mr-2 flex-shrink-0" />}
                <span className="font-medium flex-1">{message.text}</span>
                <button onClick={() => setMessage({ text: '', type: '' })} className="ml-auto p-1 hover:bg-white hover:bg-opacity-30 rounded">
                  <X className="w-3 h-3" />
                </button>
              </div>
            </div>
          )}

          {activeProvider ? (
            /* ── Catálogo del proveedor (tabla ancha, estilo Pedidos) ─────── */
            <div className="bg-white rounded-xl shadow-lg overflow-hidden border border-gray-100 h-full flex flex-col">

              {/* Filtros */}
              <div className="flex-shrink-0 p-4 border-b border-gray-200 grid grid-cols-1 md:grid-cols-3 gap-3">
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Search size={16} className="text-gray-400" />
                  </div>
                  <input
                    type="text"
                    value={productSearchTerm}
                    onChange={(e) => setProductSearchTerm(e.target.value)}
                    className="w-full pr-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none transition-colors"
                    style={{ paddingLeft: '40px' }}
                    onFocus={e => e.target.style.borderColor = theme.primary}
                    onBlur={e => e.target.style.borderColor = '#d1d5db'}
                    placeholder={isClient ? "Buscar productos (ej: GASA 45)..." : "Buscar por nombre, referencia o marca..."}
                  />
                </div>
                {[
                  { key: 'marca', placeholder: 'Filtrar por marca...' },
                  { key: 'familia', placeholder: 'Filtrar por familia...' },
                ].map(f => (
                  <input
                    key={f.key}
                    type="text"
                    value={productFilters[f.key]}
                    onChange={(e) => setProductFilters(prev => ({...prev, [f.key]: e.target.value}))}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none transition-colors"
                    onFocus={e => e.target.style.borderColor = theme.primary}
                    onBlur={e => e.target.style.borderColor = '#d1d5db'}
                    placeholder={f.placeholder}
                  />
                ))}
              </div>

              {/* Tabla */}
              {loadingProducts ? (
                <div className="flex-1 flex flex-col items-center justify-center py-12">
                  <div
                    className="w-8 h-8 border-4 border-t-transparent rounded-full animate-spin mb-4"
                    style={{ borderColor: theme.primary, borderTopColor: 'transparent' }}
                  ></div>
                  <p className="text-gray-600">Cargando productos...</p>
                </div>
              ) : filteredProducts.length === 0 ? (
                <div className="flex-1 flex flex-col items-center justify-center py-12 text-gray-500">
                  <Package size={40} className="mb-3 text-gray-300" />
                  <p className="font-medium">No se encontraron productos</p>
                  {productSearchTerm && <p className="text-sm mt-1">para la búsqueda: "{productSearchTerm}"</p>}
                </div>
              ) : (
                <div className="flex-1 overflow-auto">
                  <table className="w-full table-auto divide-y divide-gray-200">
                    <thead className="sticky top-0 z-10">
                      <tr>
                        <th className="w-12 px-4 py-3 text-left text-xs font-semibold text-white uppercase tracking-wider" style={bgPrimary}>#</th>
                        {[
                          { label: 'Referencia', field: 'ref_proveedor' },
                          { label: 'Producto', field: 'nombre' },
                          { label: 'Marca', field: 'marca' },
                          { label: 'Familia', field: 'familia' },
                        ].map(col => (
                          <th
                            key={col.field}
                            className="px-4 py-3 text-left text-xs font-semibold text-white uppercase tracking-wider cursor-pointer select-none"
                            style={bgPrimary}
                            onClick={() => handleProductSort(col.field)}
                          >
                            <div className="flex items-center">
                              {col.label}
                              <ArrowUpDown size={14} className="ml-1 opacity-70" />
                              {productSortField === col.field && (productSortDirection === 'asc' ? <ChevronUp size={14} /> : <ChevronDown size={14} />)}
                            </div>
                          </th>
                        ))}
                        <th className="px-4 py-3 text-left text-xs font-semibold text-white uppercase tracking-wider" style={bgPrimary}>Unidad</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-white uppercase tracking-wider" style={bgPrimary}>Embalaje</th>
                        {isProveedor && (
                          <th className="px-4 py-3 text-left text-xs font-semibold text-white uppercase tracking-wider" style={bgPrimary}>Clientes</th>
                        )}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100 bg-white">
                      {filteredProducts.map((product, index) => (
                        <tr key={product.id} className={`hover:bg-gray-50 transition-colors ${index % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'}`}>
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-400">
                            {index + 1}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500 font-mono">
                            {highlightSearchTerm(product.ref_proveedor || '-', productSearchTerm)}
                          </td>
                          <td className="px-4 py-3 text-sm font-medium text-gray-800">
                            {highlightSearchTerm(product.nombre, productSearchTerm)}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">
                            {highlightSearchTerm(product.marca || '-', productSearchTerm)}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">
                            {highlightSearchTerm(product.familia || '-', productSearchTerm)}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">{product.unidadbasica || '-'}</td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">{product.cantidad_embalaje || '-'}</td>
                          {isProveedor && (
                            <td className="px-4 py-3 text-sm text-gray-600">
                              {product.clientes_asociados ? (
                                <div className="flex flex-wrap gap-1">
                                  {product.clientes_asociados.split(',').map((cliente, idx) => (
                                    <span key={idx} className="inline-block text-xs px-2 py-0.5 rounded" style={{ backgroundColor: theme.primaryLight, color: theme.primaryDark }}>
                                      {cliente.trim()}
                                    </span>
                                  ))}
                                </div>
                              ) : (
                                <span className="text-gray-400">Sin clientes</span>
                              )}
                            </td>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

            </div>

          ) : isAdmin ? (
            /* ── Admin sin proveedor: invitar a seleccionar uno ───────────── */
            <div className="h-full flex items-center justify-center text-center">
              <div className="max-w-md">
                <div className="p-4 rounded-full mb-4 inline-flex" style={bgPrimaryLight}>
                  <Building size={32} style={textPrimary} />
                </div>
                <h3 className="text-lg font-semibold text-gray-700">Selecciona un proveedor</h3>
                <p className="text-sm text-gray-500 mt-2">
                  Elige un proveedor en el panel de la izquierda para ver y buscar en su catálogo completo de productos.
                </p>
              </div>
            </div>

          ) : (
            /* ── Vista proveedor (rol proveedor): su propio catálogo ───────── */
            <div className="max-w-7xl mx-auto">
              <div className="bg-white rounded-xl shadow-lg border border-gray-100">
                <div className="p-6">
                  <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                    <Package size={20} className="mr-2" style={textPrimary} />
                    Catálogo de Productos
                    <span className="ml-2 text-xs px-2 py-1 rounded-full" style={{ backgroundColor: theme.primaryLight, color: theme.primaryDark }}>
                      {filteredProducts.length} productos
                    </span>
                  </h3>

                  {/* Filtros */}
                  <div className="mb-6 grid grid-cols-1 md:grid-cols-5 gap-4">
                    <div>
                      <input
                        type="text"
                        value={productSearchTerm}
                        onChange={(e) => setProductSearchTerm(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none"
                        onFocus={e => e.target.style.borderColor = theme.primary}
                        onBlur={e => e.target.style.borderColor = '#d1d5db'}
                        placeholder={isClient ? "Buscar productos (ej: GASA 45)..." : "Buscar productos..."}
                      />
                      {isClient && <p className="text-xs text-gray-500 mt-1">Búsqueda estricta por nombre de producto</p>}
                    </div>
                    {[
                      { key: 'precio_min', placeholder: 'Precio mín €', type: 'number' },
                      { key: 'precio_max', placeholder: 'Precio máx €', type: 'number' },
                      { key: 'marca', placeholder: 'Filtrar por marca...', type: 'text' },
                      { key: 'familia', placeholder: 'Filtrar por familia...', type: 'text' },
                    ].map(f => (
                      <div key={f.key}>
                        <input
                          type={f.type}
                          value={productFilters[f.key]}
                          onChange={(e) => setProductFilters(prev => ({...prev, [f.key]: e.target.value}))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none"
                          onFocus={e => e.target.style.borderColor = theme.primary}
                          onBlur={e => e.target.style.borderColor = '#d1d5db'}
                          placeholder={f.placeholder}
                          step={f.type === 'number' ? '0.01' : undefined}
                          min={f.type === 'number' ? '0' : undefined}
                        />
                      </div>
                    ))}
                    {(productSearchTerm || productFilters.precio_min || productFilters.precio_max || productFilters.marca || productFilters.familia) && (
                      <div className="md:col-span-5">
                        <button
                          onClick={() => {
                            setProductSearchTerm('');
                            setProductFilters({ precio_min: '', precio_max: '', marca: '', familia: '' });
                          }}
                          className="text-gray-600 hover:text-gray-800 text-sm flex items-center"
                        >
                          <X size={16} className="mr-1" />
                          Limpiar filtros
                        </button>
                      </div>
                    )}
                  </div>

                  {loadingProducts ? (
                    <div className="text-center py-12">
                      <div
                        className="w-12 h-12 border-4 border-t-transparent rounded-full animate-spin mx-auto mb-4"
                        style={{ borderColor: theme.primary, borderTopColor: 'transparent' }}
                      ></div>
                      <p className="text-gray-600 text-lg">Cargando productos...</p>
                    </div>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full table-auto">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">#</th>
                            {[
                              { label: 'Producto', field: 'nombre' },
                              { label: 'Referencia', field: 'ref_proveedor' },
                              { label: 'Marca', field: 'marca' },
                              { label: 'Familia', field: 'familia' },
                            ].map(col => (
                              <th
                                key={col.field}
                                className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                                onClick={() => handleProductSort(col.field)}
                              >
                                <div className="flex items-center">
                                  {col.label}
                                  <ArrowUpDown size={14} className="ml-1" />
                                  {productSortField === col.field && (productSortDirection === 'asc' ? <ChevronUp size={14} /> : <ChevronDown size={14} />)}
                                </div>
                              </th>
                            ))}
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Unidad</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Embalaje</th>
                            <th
                              className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                              onClick={() => handleProductSort('precio_coste')}
                            >
                              <div className="flex items-center">
                                Precio (€) ↑ <ArrowUpDown size={14} className="ml-1" />
                                {productSortField === 'precio_coste' && (productSortDirection === 'asc' ? <ChevronUp size={14} /> : <ChevronDown size={14} />)}
                              </div>
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Proveedor</th>
                            {isProveedor && (
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Clientes</th>
                            )}
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {getCurrentPageProducts().map((product, index) => (
                            <tr key={product.id} className="hover:bg-gray-50 transition-colors duration-150">
                              <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                                {((currentProductPage - 1) * productsPerPage) + index + 1}
                              </td>
                              <td className="px-4 py-4 text-sm text-gray-900">
                                <strong>{highlightSearchTerm(product.nombre, productSearchTerm)}</strong>
                              </td>
                              <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-600">
                                {highlightSearchTerm(product.ref_proveedor || '-', productSearchTerm)}
                              </td>
                              <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-600">
                                {highlightSearchTerm(product.marca || '-', productSearchTerm)}
                              </td>
                              <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-600">
                                {highlightSearchTerm(product.familia || '-', productSearchTerm)}
                              </td>
                              <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-600">{product.unidadbasica || '-'}</td>
                              <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-600">{product.cantidad_embalaje || '-'}</td>
                              <td className="px-4 py-4 whitespace-nowrap text-sm font-bold" style={{ color: theme.primaryDark, backgroundColor: theme.primaryLight }}>
                                {formatPrice(product.precio_coste)}
                              </td>
                              <td className="px-4 py-4 whitespace-nowrap">
                                <span className="inline-block text-white text-xs px-2 py-1 rounded text-center" style={bgPrimary}>
                                  {product.proveedor_nombre || 'N/A'}
                                </span>
                              </td>
                              {isProveedor && (
                                <td className="px-4 py-4 text-sm text-gray-600">
                                  {product.clientes_asociados ? (
                                    <div className="flex flex-wrap gap-1">
                                      {product.clientes_asociados.split(',').map((cliente, idx) => (
                                        <span key={idx} className="inline-block bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded">
                                          {cliente.trim()}
                                        </span>
                                      ))}
                                    </div>
                                  ) : (
                                    <span className="text-gray-400">Sin clientes</span>
                                  )}
                                </td>
                              )}
                            </tr>
                          ))}
                        </tbody>
                      </table>

                      {filteredProducts.length === 0 && !loadingProducts && (
                        <div className="text-center py-12 text-gray-500">
                          <Package size={48} className="mx-auto mb-4 text-gray-400" />
                          <p className="text-lg font-medium">No se encontraron productos</p>
                          {productSearchTerm && (
                            <p className="text-sm mt-2">No se encontraron productos que contengan: <strong>"{productSearchTerm}"</strong></p>
                          )}
                          <p className="text-sm mt-2">Intente modificar los filtros o contacte con su proveedor.</p>
                        </div>
                      )}
                    </div>
                  )}

                  {totalProductPages > 1 && <PaginationControls />}

                  {filteredProducts.length > 0 && (
                    <div className="mt-6 p-4 rounded-lg" style={bgPrimaryLight}>
                      <div className="flex justify-between items-center">
                        <div className="flex items-center text-sm">
                          <Check className="w-4 h-4 mr-2" style={textPrimary} />
                          <span className="font-medium" style={textPrimaryDark}>
                            Total de productos encontrados: {filteredProducts.length}
                          </span>
                        </div>
                        {productSearchTerm && (
                          <span className="text-sm" style={textPrimary}>Búsqueda: "{productSearchTerm}"</span>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </main>

      {/* ── Modal Crear/Editar Proveedor ─────────────────────────────────── */}
      {isAdmin && (showCreateForm || editingProvider) && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden">

            {/* Header modal */}
            <div className="text-white p-6 border-b" style={bgPrimary}>
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <div className="p-2 rounded-lg mr-3" style={{ backgroundColor: 'rgba(255,255,255,0.2)' }}>
                    {editingProvider ? <Edit size={20} /> : <Plus size={20} />}
                  </div>
                  <div>
                    <h2 className="text-xl font-bold">
                      {editingProvider ? 'Editar Proveedor' : 'Nuevo Proveedor'}
                    </h2>
                    <p className="text-sm" style={{ color: 'rgba(255,255,255,0.8)' }}>
                      {editingProvider ? 'Modifique la información del proveedor' : 'Complete la información del nuevo proveedor'}
                    </p>
                  </div>
                </div>
                <button
                  onClick={cancelEdit}
                  className="p-2 rounded-lg transition-all duration-200 hover:bg-red-500 text-white"
                >
                  <X size={20} />
                </button>
              </div>
            </div>

            {/* Contenido del modal */}
            <div className="p-6 overflow-y-auto max-h-[calc(90vh-140px)]">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">

                <div className="md:col-span-2">
                  <label className="block text-sm font-bold text-gray-700 mb-2">
                    <Building className="w-4 h-4 inline mr-2" />
                    Nombre del Proveedor*
                  </label>
                  <input
                    type="text"
                    value={formData.nombre}
                    onChange={(e) => handleInputChange('nombre', e.target.value)}
                    placeholder="Nombre o razón social del proveedor"
                    className={`w-full px-3 py-2 border-2 rounded-lg focus:outline-none transition-all ${formErrors.nombre ? 'border-red-500' : 'border-gray-300'}`}
                    onFocus={e => !formErrors.nombre && (e.target.style.borderColor = theme.primary)}
                    onBlur={e => !formErrors.nombre && (e.target.style.borderColor = '#e5e7eb')}
                  />
                  {formErrors.nombre && <p className="text-red-500 text-xs mt-1">{formErrors.nombre}</p>}
                </div>

                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2">
                    <Mail className="w-4 h-4 inline mr-2" />Email
                  </label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                    placeholder="correo@proveedor.com"
                    className={`w-full px-3 py-2 border-2 rounded-lg focus:outline-none transition-all ${formErrors.email ? 'border-red-500' : 'border-gray-300'}`}
                    onFocus={e => !formErrors.email && (e.target.style.borderColor = theme.primary)}
                    onBlur={e => !formErrors.email && (e.target.style.borderColor = '#e5e7eb')}
                  />
                  {formErrors.email && <p className="text-red-500 text-xs mt-1">{formErrors.email}</p>}
                </div>

                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2">
                    <Phone className="w-4 h-4 inline mr-2" />Teléfono
                  </label>
                  <input
                    type="tel"
                    value={formData.telefono}
                    onChange={(e) => handleInputChange('telefono', e.target.value)}
                    placeholder="+34 912 345 678"
                    className={`w-full px-3 py-2 border-2 rounded-lg focus:outline-none transition-all ${formErrors.telefono ? 'border-red-500' : 'border-gray-300'}`}
                    onFocus={e => !formErrors.telefono && (e.target.style.borderColor = theme.primary)}
                    onBlur={e => !formErrors.telefono && (e.target.style.borderColor = '#e5e7eb')}
                  />
                  {formErrors.telefono && <p className="text-red-500 text-xs mt-1">{formErrors.telefono}</p>}
                </div>

                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2">
                    <Euro className="w-4 h-4 inline mr-2" />Precio Mínimo (€)
                  </label>
                  <input
                    type="number"
                    step="0.01" min="0"
                    value={formData.precio_min}
                    onChange={(e) => handleInputChange('precio_min', e.target.value)}
                    placeholder="0.00"
                    className={`w-full px-3 py-2 border-2 rounded-lg focus:outline-none transition-all ${formErrors.precio_min ? 'border-red-500' : 'border-gray-300'}`}
                    onFocus={e => !formErrors.precio_min && (e.target.style.borderColor = theme.primary)}
                    onBlur={e => !formErrors.precio_min && (e.target.style.borderColor = '#e5e7eb')}
                  />
                  {formErrors.precio_min && <p className="text-red-500 text-xs mt-1">{formErrors.precio_min}</p>}
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-bold text-gray-700 mb-2">
                    <MapPin className="w-4 h-4 inline mr-2" />Dirección
                  </label>
                  <textarea
                    value={formData.direccion}
                    onChange={(e) => handleInputChange('direccion', e.target.value)}
                    placeholder="Dirección completa del proveedor"
                    rows={3}
                    className="w-full px-3 py-2 border-2 rounded-lg focus:outline-none transition-all border-gray-300 resize-vertical"
                    onFocus={e => e.target.style.borderColor = theme.primary}
                    onBlur={e => e.target.style.borderColor = '#e5e7eb'}
                  />
                </div>

                {/* Fuerza el visto bueno de la matriz para este proveedor concreto,
                    con independencia de si la red tiene el flujo de aprobación
                    general activado. */}
                <div className="md:col-span-2">
                  <label
                    className="flex items-start gap-3 p-4 border-2 border-gray-300 rounded-lg cursor-pointer transition-all hover:bg-gray-50"
                  >
                    <input
                      type="checkbox"
                      checked={!!formData.requiere_aprobacion_matriz}
                      onChange={(e) => handleInputChange('requiere_aprobacion_matriz', e.target.checked)}
                      className="mt-1 w-4 h-4 cursor-pointer"
                      style={{ accentColor: theme.primary }}
                    />
                    <span>
                      <span className="block text-sm font-bold text-gray-700">
                        <AlertCircle className="w-4 h-4 inline mr-2" />
                        Requiere aprobación de la clínica matriz
                      </span>
                      <span className="block text-xs text-gray-500 mt-1">
                        Si se marca, todos los pedidos que las clínicas hijas hagan a
                        este proveedor quedarán en <strong>pendiente de aprobación</strong>,
                        para que la clínica matriz los confirme antes de enviarlos —
                        aunque la red no tenga activado el flujo de aprobación general.
                        El pedido sigue siendo de la sede que lo hizo: el gasto y la
                        facturación no cambian. Los pedidos que la propia matriz (o una
                        clínica independiente) haga a este proveedor no se ven afectados.
                      </span>
                    </span>
                  </label>
                </div>
              </div>

              {/* Botones del modal */}
              <div className="flex justify-center gap-4 mt-8">
                <button
                  onClick={cancelEdit}
                  className="px-6 py-3 rounded-lg font-medium transition-all border border-gray-300 text-gray-700 hover:bg-gray-50"
                >
                  Cancelar
                </button>
                <button
                  onClick={editingProvider ? handleUpdateProvider : handleCreateProvider}
                  disabled={loading}
                  className={`px-6 py-3 rounded-lg font-medium transition-all flex items-center text-white ${loading ? 'opacity-50 cursor-not-allowed' : 'hover:opacity-90'}`}
                  style={bgPrimary}
                  onMouseEnter={e => { if (!loading) e.currentTarget.style.backgroundColor = theme.primaryDark }}
                  onMouseLeave={e => { if (!loading) e.currentTarget.style.backgroundColor = theme.primary }}
                >
                  {loading ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                      {editingProvider ? 'Actualizando...' : 'Creando...'}
                    </>
                  ) : (
                    <>
                      <Save className="w-4 h-4 mr-2" />
                      {editingProvider ? 'Actualizar Proveedor' : 'Crear Proveedor'}
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>

          <ImageModal
            isOpen={modalImagenVisible}
            imageSrc={imagenSeleccionada.src}
            imageAlt={imagenSeleccionada.alt}
            onClose={handleCloseImageModal}
          />
        </div>
      )}

      <BarcodesBulkModal
        open={barcodesBulkModal.open}
        provider={barcodesBulkModal.provider}
        theme={theme}
        onClose={() => setBarcodesBulkModal({ open: false, provider: null })}
      />
    </div>
  );
}